import dayjs from 'dayjs';
import {
  Award,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  Quote,
  Star,
  X,
} from 'lucide-react';
import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { calculateAverageUserRating } from './api/commonUtils';
import { refreshMovie, saveRating, setWatchlist } from './api/movies';
import { useSWRCacheManager } from './api/swrCacheManager';
import {
  OpenInNewPageActionButton,
  RatingActionButton,
  RefreshActionButton,
  WatchlistActionButton,
} from './components/ActionButtons';
import { FadeInImage } from './components/FadeInImage';
import { ReleaseDateBadge } from './components/ReleaseDateBadge';
import {
  getAgoDate,
  getRating,
  getRatingTier,
  getWatchlistDate,
  isWatchlisted,
  setRatingInfo,
  setWatchlistDate,
} from './helpers';
import {
  useLatestActivity,
  useMovieById,
  useRatingsOrWatchlist,
} from './hooks/useMovieData';
import { useMovieModal } from './MovieModalContext';
import { SetRatingPopup } from './SetRatingPopup';
import { UserContext } from './UserContext';
import YoutubeEmbed from './YoutubeEmbed';

export function MoviePage({ movieId: propMovieId = null, isModal = false }) {
  const params = useParams();
  const movieId = propMovieId || params.movieId;
  const { closeMovie, notifyMovieUpdated } = useMovieModal();
  const user_data = useContext(UserContext);
  const [showAllCast, setShowAllCast] = useState(false);
  const [showAllMedia, setShowAllMedia] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const [selectedMovie, setSelectedMovie] = useState(null);
  const { movie: fetchedMovie } = useMovieById(
    movieId,
    'credits,videos,images',
  );
  const { mutate: mutateActivity } = useLatestActivity();
  const { mutate: mutateUserRatings } = useRatingsOrWatchlist(
    user_data.username,
    true,
  );
  const { mutate: mutateUserWatchlist } = useRatingsOrWatchlist(
    user_data.username,
    false,
  );
  const { invalidateMovieCaches } = useSWRCacheManager();

  const handlePopoverOpen = (event) => {
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handlePopoverClose = () => {
    setOpen(false);
  };

  function toggleWatchlist(event) {
    event?.stopPropagation();
    const userId = Number(user_data.id);
    const current_date = getWatchlistDate(selectedMovie, userId);
    const watchlisted = isWatchlisted(selectedMovie, userId);
    var info = {
      watchlist_date: current_date ? current_date : dayjs(new Date()),
      watchlisted: !watchlisted,
      movie_id: selectedMovie.id,
      user_id: userId,
    };
    setWatchlist(info).then(() => {
      const updatedMovie = {
        ...selectedMovie,
        user_ratings: setWatchlistDate(selectedMovie, info, user_data),
      };
      setSelectedMovie(updatedMovie);
      notifyMovieUpdated(updatedMovie);
      // Invalidate all movie-related caches
      invalidateMovieCaches(selectedMovie.id);
      mutateActivity();
      mutateUserWatchlist();
    });
  }

  function handleRatingSave(info) {
    saveRating(info).then(() => {
      const ratings = setRatingInfo(selectedMovie, info, user_data);
      const updatedMovie = {
        ...selectedMovie,
        user_ratings: ratings,
        user_average_rating: calculateAverageUserRating(ratings),
      };
      setSelectedMovie(updatedMovie);
      notifyMovieUpdated(updatedMovie);
      // Invalidate all movie-related caches
      invalidateMovieCaches(selectedMovie.id);
      mutateActivity();
      mutateUserRatings();
    });
  }

  async function handleRefresh(event) {
    event?.stopPropagation();
    const refreshedMovie = await refreshMovie(selectedMovie.id);
    // Preserve extra info (credits, videos, images) from the old data
    const updatedMovie = {
      ...refreshedMovie,
      credits: selectedMovie.credits,
      videos: selectedMovie.videos,
      images: selectedMovie.images,
    };
    setSelectedMovie(updatedMovie);
    notifyMovieUpdated(updatedMovie);
    // Invalidate all movie-related caches to update listings
    invalidateMovieCaches(selectedMovie.id);
  }

  useEffect(() => {
    if (!fetchedMovie) {
      return;
    }
    setSelectedMovie((prev) => {
      if (!prev || prev.id !== fetchedMovie.id) {
        return fetchedMovie;
      }
      return prev;
    });
  }, [fetchedMovie]);

  if (!selectedMovie) {
    return null;
  }

  function getNormalizedRatingValueBySource(rating) {
    var ratingValue = rating.value;
    if (!ratingValue) return null;
    let value = parseFloat(
      ratingValue.toString().replace('%', '').split('/')[0],
    );
    if (isNaN(value)) return null;

    if (['TheMovieDB', 'Internet Movie Database'].includes(rating.source)) {
      value *= 100 / 10;
    }

    return value;
  }

  function getPosterUrl(path, size = 'w500') {
    if (!path) return '/img/noPhoto.webp';
    return `https://image.tmdb.org/t/p/${size}${path}`;
  }

  function formatRuntime(runtime) {
    if (!runtime || Number.isNaN(Number(runtime))) return 'N/A';
    const totalMinutes = Number(runtime);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (!hours) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  function getUserStarRating() {
    if (selectedMovie.user_ratings && selectedMovie.user_ratings.length > 0) {
      const avgRating =
        selectedMovie.user_ratings.reduce(
          (sum, ur) => sum + (ur.rating || 0),
          0,
        ) / selectedMovie.user_ratings.filter((ur) => ur.rating).length;
      return isNaN(avgRating) ? null : parseFloat(avgRating.toFixed(1));
    }
    return null;
  }

  const posterUrl = getPosterUrl(selectedMovie.poster_path);
  // Use poster as hero to avoid duplicate image load
  const heroUrl = posterUrl;
  const externalRating = selectedMovie.average_rating || null;
  const runtimeLabel = formatRuntime(selectedMovie.runtime);
  const userStarRating = getUserStarRating();

  const directorCrew = selectedMovie.credits?.crew
    ? selectedMovie.credits.crew
        .filter((info) => info.job == 'Director' || info.job == 'Writer')
        .sort((info) => -info.popularity)
    : [];

  const allCastMembers = selectedMovie.credits?.cast
    ? selectedMovie.credits.cast
        .filter((info) => info.known_for_department == 'Acting')
        .sort((info) => -info.popularity)
    : [];
  const castMembers = showAllCast ? allCastMembers : allCastMembers.slice(0, 6);
  const hasMoreCast = allCastMembers.length > 6;

  const allVideos =
    selectedMovie.videos?.results?.filter(
      (v) => v.site === 'YouTube' && v.type === 'Trailer',
    ) || [];
  const allImages = selectedMovie.images?.backdrops || [];
  const totalGalleryItems = allVideos.length + allImages.length;

  const modalContent = (
    <div className="movie-modal__panel">
      <button
        className="movie-modal__close"
        onClick={closeMovie}
        aria-label="Close movie details"
      >
        <X size={18} />
      </button>

      <div className="movie-modal__hero">
        <FadeInImage
          src={heroUrl}
          alt={selectedMovie.title}
          className="movie-modal__heroImage"
          loading="eager"
          onLoad={undefined}
          onError={undefined}
        />
        <div className="movie-modal__heroOverlay" />

        <div className="movie-modal__heroContent">
          <div className="movie-modal__poster">
            <FadeInImage
              src={posterUrl}
              alt={selectedMovie.title}
              loading="eager"
              onLoad={undefined}
              onError={undefined}
            />
          </div>
          <div className="movie-modal__header">
            <h2 className="movie-modal__title">{selectedMovie.title}</h2>

            <div className="movie-modal__metaInfo">
              <div className="movie-modal__ratingsBadges">
                {userStarRating !== null && (
                  <div
                    className={`modal-ratingBadge modal-ratingBadge--star modal-ratingBadge--${getRatingTier(userStarRating * 20)}`}
                  >
                    <Star size={16} fill="currentColor" />
                    <span>{userStarRating.toFixed(1)}</span>
                  </div>
                )}
                {externalRating !== null && (
                  <div
                    className={`modal-ratingBadge modal-ratingBadge--external modal-ratingBadge--${getRatingTier(externalRating)}`}
                  >
                    <Award size={16} />
                    <span>{Math.round(externalRating)}</span>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <RatingActionButton
                  userRating={getRating(selectedMovie, Number(user_data.id))}
                  onClick={handlePopoverOpen}
                />
                <SetRatingPopup
                  movie={selectedMovie}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  onRatingChanged={handleRatingSave}
                />
                <WatchlistActionButton
                  isWatchlisted={isWatchlisted(
                    selectedMovie,
                    Number(user_data.id),
                  )}
                  onClick={toggleWatchlist}
                />
                <RefreshActionButton onClick={handleRefresh} />
                <OpenInNewPageActionButton movieId={selectedMovie.id} />
              </div>
            </div>

            <div className="movie-modal__meta">
              <div className="movie-modal__metaItem">
                <Calendar size={16} />
                <ReleaseDateBadge
                  releaseDate={selectedMovie.release_date}
                  releaseYear={selectedMovie.release_year}
                />
              </div>
              <div className="movie-modal__metaItem">
                <Clock size={16} />
                <span>{runtimeLabel}</span>
              </div>
              {selectedMovie.genres?.map((genre) => (
                <span key={genre.id} className="movie-modal__genrePill">
                  {genre.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="movie-modal__content">
        <section className="movie-modal__section">
          <h3 className="movie-modal__sectionTitle">Overview</h3>
          <p className="movie-modal__overview">{selectedMovie.overview}</p>
        </section>

        <section className="movie-modal__section">
          <div className="activity-layout">
            <div className="activity-column">
              <h3 className="movie-modal__sectionTitle">Recent Activity</h3>
              <div className="activity-list">
                {(() => {
                  if (selectedMovie.user_ratings?.length === 0) {
                    return (
                      <div
                        style={{
                          color: '#666',
                          fontStyle: 'italic',
                        }}
                      >
                        No activity yet.
                      </div>
                    );
                  }

                  return selectedMovie.user_ratings
                    .filter(
                      (activity) => activity.rating || activity.watchlisted,
                    )
                    .map((activity, index) => (
                      <div
                        key={`${activity.username}_${index}`}
                        className={`activity-item ${
                          activity.user_id === Number(user_data.id)
                            ? 'activity-item--current'
                            : ''
                        }`}
                      >
                        <div
                          className={`activity-icon ${
                            activity.rating
                              ? 'activity-icon--rating'
                              : 'activity-icon--watchlist'
                          }`}
                        >
                          {activity.rating ? (
                            <Star size={14} fill="currentColor" />
                          ) : (
                            <Eye size={14} />
                          )}
                        </div>
                        <div className="activity-content">
                          <div className="activity-main">
                            <span className="activity-username">
                              {activity.display_name ?? activity.username}
                            </span>
                            {activity.rating && (
                              <span className="activity-action">
                                {' '}
                                rated{' '}
                                <span
                                  className={`activity-rating activity-rating--${getRatingTier(activity.rating * 20)}`}
                                >
                                  {activity.rating}
                                </span>
                              </span>
                            )}
                            {activity.watchlisted && !activity.rating && (
                              <span className="activity-action">
                                {' '}
                                added to watchlist
                              </span>
                            )}
                            {activity.comment && (
                              <span className="activity-comment">
                                <Quote
                                  size={12}
                                  className="activity-quote-icon"
                                />
                                {activity.comment}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="activity-time">
                          {getAgoDate(
                            activity.rating_date ?? activity.watchlist_date,
                          )}
                        </span>
                      </div>
                    ));
                })()}
              </div>
            </div>
            <div className="movie-modal__ratingsColumn">
              <h3 className="movie-modal__sectionTitle">Ratings</h3>
              <div className="movie-modal__externalRatingsList">
                {selectedMovie.ratings && selectedMovie.ratings.length > 0 ? (
                  selectedMovie.ratings.map((rating, index) => {
                    const getRatingIcon = () => {
                      return (
                        <FadeInImage
                          style={{ width: '16px', height: '16px' }}
                          src={rating.image}
                          alt={rating.source}
                          loading="eager"
                          onLoad={undefined}
                          onError={undefined}
                        />
                      );
                    };

                    return (
                      <div
                        key={index}
                        className="movie-modal__externalRatingItem"
                        title={rating.source}
                      >
                        <div
                          className={`modal-ratingBadge modal-ratingBadge--external modal-ratingBadge--${getRatingTier(getNormalizedRatingValueBySource(rating))}`}
                        >
                          {getRatingIcon()}
                          <span>
                            {typeof rating.value === 'string'
                              ? rating.value
                              : rating.value.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      color: '#666',
                      fontStyle: 'italic',
                    }}
                  >
                    No ratings yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {(directorCrew.length > 0 || castMembers.length > 0) && (
          <section className="movie-modal__section">
            <h3 className="movie-modal__sectionTitle">Cast & Crew</h3>
            <div className="movie-modal__castGrid">
              {directorCrew.map((person, i) => (
                <PersonView person={person} key={`${person.id}_${i}`} />
              ))}
              {castMembers.map((person, i) => (
                <PersonView person={person} key={`${person.id}_${i}`} />
              ))}
            </div>
            {hasMoreCast && (
              <button
                onClick={() => setShowAllCast(!showAllCast)}
                className="movie-modal__expandButton"
              >
                {showAllCast ? (
                  <>
                    <ChevronUp size={16} />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} />
                    See {allCastMembers.length - 6} more
                  </>
                )}
              </button>
            )}
          </section>
        )}

        {totalGalleryItems > 0 && (
          <section className="movie-modal__section">
            <h3 className="movie-modal__sectionTitle">Gallery</h3>
            <div className="movie-modal__gallery">
              {(() => {
                const allGalleryItems = [
                  ...allVideos.map((v) => ({
                    type: 'video',
                    data: v,
                  })),
                  ...allImages.map((img) => ({
                    type: 'image',
                    data: img,
                  })),
                ];
                const displayedItems = showAllMedia
                  ? allGalleryItems
                  : allGalleryItems.slice(0, 6);

                return displayedItems.map((item, index) => (
                  <div
                    key={`${item.type}-${index}`}
                    className="movie-modal__galleryItem"
                  >
                    {item.type === 'video' ? (
                      <YoutubeEmbed
                        embedId={item.data.key}
                        title={item.data.title}
                      />
                    ) : (
                      <a
                        href={`https://image.tmdb.org/t/p/original${item.data.file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="movie-modal__galleryLink"
                      >
                        <FadeInImage
                          src={`https://image.tmdb.org/t/p/w300${item.data.file_path}`}
                          alt={`${selectedMovie.title} scene ${index + 1}`}
                          loading="lazy"
                          onLoad={undefined}
                          onError={undefined}
                        />
                      </a>
                    )}
                  </div>
                ));
              })()}
            </div>
            {totalGalleryItems > 6 && (
              <button
                onClick={() => setShowAllMedia(!showAllMedia)}
                className="movie-modal__expandButton"
              >
                {showAllMedia ? (
                  <>
                    <ChevronUp size={16} />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} />
                    See {totalGalleryItems - 6} more
                  </>
                )}
              </button>
            )}
          </section>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return modalContent;
  }

  return <div className="movie-modal">{modalContent}</div>;

  function PersonView({ person }) {
    return (
      <Link
        to={`/p/${person.id}/${person.name}`}
        className="movie-modal__castItem"
        onClick={closeMovie}
      >
        <div className="movie-modal__castItem__castImageWrapper">
          <FadeInImage
            src={getPosterUrl(person.profile_path, 'w92')}
            alt={person.name}
            loading="lazy"
            onLoad={undefined}
            onError={undefined}
          />
        </div>
        <div className="movie-modal__castInfo">
          <p className="movie-modal__castName">{person.name}</p>
          <p className="movie-modal__castRole">
            {person.character || person.job || person.known_for_department}
          </p>
        </div>
      </Link>
    );
  }
}
